import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SeriesOptionsType } from 'highcharts';
import { Alert, Card, Space, Typography } from 'antd';
import { useTheme } from '../../../../../../../providers/theme';
import { getColors } from '../../../../../../../components/utils/getColors';
import ChartSettings from '../../../../../../../components/chart/settings';
import { ChartSettingsProps } from '../../../../../../../@types/data/types';
import { HfuBand } from '../../../../../../../@types/projects/types';
import { ProjectSample } from '../../../../../../../@types/samples/types';
import { computeStratigraphicLorenz } from './utils';
import { Chart } from '../../../../../../../components/chart/BaseChart';
import { ChartSpec } from '../../../../../../../components/chart/chartSpec';
import { CHART_BOOST_ENABLED, EXPERIMENT_BOOST } from '../../../../../../../components/chart/boost';

interface StratigraphicLorenzChartProps {
  samples: ProjectSample[];
  hfuBands?: HfuBand[];
  containerWidth?: string | number;
  height?: string | number;
}

const StratigraphicLorenzChart: React.FC<StratigraphicLorenzChartProps> = ({
  samples,
  hfuBands,
  containerWidth,
  height,
}) => {
  const { isDay } = useTheme();
  const colors = getColors(isDay);
  const chartRef = useRef<any>(null);

  const usingDefaults = !hfuBands || hfuBands.length === 0;
  const [bannerDismissed, setBannerDismissed] = useState(false);

  useEffect(() => {
    if (usingDefaults) setBannerDismissed(false);
  }, [usingDefaults]);

  const lorenzResult = useMemo(() => computeStratigraphicLorenz(samples, hfuBands), [samples, hfuBands]);

  const chartSeries = useMemo((): SeriesOptionsType[] => {
    const fullCurve: SeriesOptionsType = {
      id: 'polok-1',
      name: 'Polok-1',
      type: 'line',
      color: '#3f51ff',
      zIndex: 2,
      lineWidth: 1,
      data: lorenzResult.points.map((p) => ({
        x: p.storageNorm,
        y: p.flowNorm,
        name: p.sampleCode,
        custom: p,
      })),
      marker: {
        enabled: true,
        radius: 3,
        symbol: 'circle',
        fillColor: '#d9e2ff',
        lineColor: '#273cff',
        lineWidth: 1,
      },
    };

    const hfuSeries: SeriesOptionsType[] = lorenzResult.hfuSeries.map(
      ({ hfu, color, points }) => ({
        id: hfu,
        name: hfu,
        type: 'scatter' as const,
        color,
        zIndex: 3,
        data: points.map((p) => ({
          x: p.storageNorm,
          y: p.flowNorm,
          name: p.sampleCode,
          custom: p,
        })),
        marker: {
          radius: 4,
          symbol: 'circle',
          fillColor: color,
          lineColor: color,
          lineWidth: 1.5,
        },
      })
    );

    return [fullCurve, ...hfuSeries];
  }, [lorenzResult]);

  const autoDefaults = useMemo<ChartSettingsProps>(() => ({
    xmin: 0,
    xmax: 1,
    ymin: 0,
    ymax: 1,
  }), []);

  const [chartSettings, setChartSettings] = useState<ChartSettingsProps>(autoDefaults);
  const [userHasSetLimits, setUserHasSetLimits] = useState(false);

  useEffect(() => {
    if (!userHasSetLimits) setChartSettings(autoDefaults);
  }, [autoDefaults, userHasSetLimits]);

  const handleSetChartSettings = useCallback((s: ChartSettingsProps) => {
    setChartSettings(s);
    setUserHasSetLimits(true);
  }, []);

  const spec: ChartSpec = useMemo(
    () => ({
      type: 'line',
      zoomType: 'xy',
      height,
      title: 'Stratigraphic Flow Units',
      xAxis: {
        title: 'Statigraphic Normalized Storage Capacity',
        min: chartSettings.xmin ?? 0,
        max: chartSettings.xmax ?? 1,
      },
      yAxis: {
        title: 'Statigraphic Normalized Flow<br/>Capacity',
        min: chartSettings.ymin ?? 0,
        max: chartSettings.ymax ?? 1,
      },
      plotOptions: {
        scatter: {
          marker: {
            symbol: 'circle',
            states: { hover: { enabled: true, lineColor: 'rgb(100,100,100)' } },
          },
        },
        line: {
          marker: { enabled: false },
        },
      },
      legend: true,
      tooltip: function () {
        const custom = (this.point as any).custom;
        if (!custom) return false;
        const storage = Number(this.x);
        const flow = Number(this.y);
        return (
          `<b>${this.series.name}</b><br/>` +
          `Sample: ${this.point.name}<br/>` +
          `HFU: ${custom.hfu}<br/>` +
          `Storage: ${Number.isFinite(storage) ? storage.toFixed(4) : ''}<br/>` +
          `Flow: ${Number.isFinite(flow) ? flow.toFixed(4) : ''}<br/>` +
          `Porosity: ${custom.porosity.toFixed(4)}<br/>` +
          `Permeability: ${custom.permeability.toFixed(4)}<br/>` +
          `Sample length: ${custom.sampleLength != null ? custom.sampleLength.toFixed(4) : 'N/A'}`
        );
      },
      boost: CHART_BOOST_ENABLED ? EXPERIMENT_BOOST : undefined,
      // gridlines + HTML y-axis title are chart-specific extras layered over the themed axes.
      options: {
        xAxis: { gridLineWidth: 1 },
        yAxis: { gridLineWidth: 1, title: { useHTML: true } },
      },
      series: chartSeries,
    }),
    [height, chartSettings, chartSeries]
  );

  return (
    <Space
      direction="vertical"
      style={{
        backgroundColor: 'transparent',
        marginTop: '1rem',
        border: `1px solid ${colors.border}`,
        borderRadius: '16px',
        transition: 'width 0.5s',
        width: containerWidth ?? '49%',
        overflow: 'hidden',
      }}
    >
      <Card size="small" style={{ backgroundColor: 'transparent', border: 'none' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 12,
          }}
        >
          <Typography.Text strong style={{ color: colors.text }}>
            Stratigraphic Flow Units
          </Typography.Text>
          <ChartSettings
            chartSettings={chartSettings}
            setChartSettings={handleSetChartSettings}
            autoDefaults={autoDefaults}
          />
        </div>
        {usingDefaults && !bannerDismissed && (
          <Alert
            type="info"
            showIcon
            closable
            onClose={() => setBannerDismissed(true)}
            message="Showing default HFU bands. Open Flow Unit Settings to customise them."
            style={{ marginBottom: 10 }}
          />
        )}
        <div
          style={{
            position: 'relative',
            borderRadius: 12,
            border: `1px solid ${colors.border}`,
            padding: 12,
          }}
        >
          <Chart spec={spec} chartRef={chartRef} />
        </div>
      </Card>
    </Space>
  );
};

export default StratigraphicLorenzChart;
